/**
 * Cognara's reading of the attachments, as suggestions against the match.
 *
 * The rule this component exists to make visible: a figure read off a
 * measurement sheet or a GRN is a SUGGESTION. It carries the document it came
 * from, the line it was quoted from, and a confidence. A human accepts it — and
 * accepting fills the MEASURED or RECEIVED quantity, never the vendor's claim.
 */
import { useCallback, useState } from 'react';
import { Check, FileSearch, Loader2, Quote, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { num } from './iptTheme';
import { Btn, Empty, Panel } from './IptPrimitives';

const CONF_TONE = (T) => ({ high: T.green, medium: T.amber, low: T.red });

export const useAnalyse = (invoiceId, onReload) => {
  const [busy, setBusy] = useState({});
  const [jobs, setJobs] = useState({});

  const poll = useCallback((documentId) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await api.get(`/ipt/documents/${documentId}/analysis`);
        setJobs((j) => ({ ...j, [documentId]: r.data }));
        if (['ready', 'failed'].includes(r.data?.status) || tries > 60) {
          setBusy((b) => ({ ...b, [documentId]: false }));
          if (r.data?.status === 'failed') toast.error(r.data?.error || 'Cognara could not read it');
          else toast.success('Cognara has read the document');
          onReload?.();
          return;
        }
      } catch (e) { /* keep polling — a single 429 should not abandon the job */ }
      setTimeout(tick, 3500);
    };
    setTimeout(tick, 2500);
  }, [onReload]);

  const analyse = useCallback(async (doc, refresh = false) => {
    setBusy((b) => ({ ...b, [doc.document_id]: true }));
    try {
      const r = await api.post(
        `/ipt/invoices/${invoiceId}/documents/${doc.document_id}/analyse`
        + (refresh ? '?refresh=true' : ''), {});
      setJobs((j) => ({ ...j, [doc.document_id]: r.data }));
      if (r.data?.status === 'ready') {
        setBusy((b) => ({ ...b, [doc.document_id]: false }));
        onReload?.();
        toast.success(r.data?.cached ? 'Already read — showing the stored reading'
          : 'Cognara has read the document');
        return;
      }
      poll(doc.document_id);
    } catch (e) {
      setBusy((b) => ({ ...b, [doc.document_id]: false }));
      toast.error(e?.response?.data?.detail || 'Cognara could not start');
    }
  }, [invoiceId, onReload, poll]);

  return { analyse, busy, jobs };
};

export const AnalyseChip = ({ doc, job, busy, onAnalyse, T }) => {
  const status = busy ? 'running' : job?.status;
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[9.5px] font-semibold"
        data-testid={`ipt-doc-analysing-${doc.document_id}`} style={{ color: T.primary }}>
        <Loader2 className="w-3 h-3 animate-spin" />
        {job?.progress ? `${job.progress}%` : 'reading'} {job?.stage ? `· ${job.stage}` : ''}
      </span>
    );
  }
  if (status === 'ready') {
    const read = job?.read || {};
    const n = (job?.suggestions || []).length;
    return (
      <button type="button" onClick={() => onAnalyse(doc, true)}
        data-testid={`ipt-doc-reread-${doc.document_id}`}
        className="inline-flex items-center gap-1 text-[9.5px] font-semibold hover:underline"
        style={{ color: read.readable === false ? T.amber : T.green }}
        title="Read again">
        <Sparkles className="w-3 h-3" />
        {read.readable === false ? 'unreadable'
          : `${read.documentKind?.replace(/_/g, ' ').toLowerCase() || 'read'} · ${n} rows`}
      </button>
    );
  }
  return (
    <button type="button" onClick={() => onAnalyse(doc)}
      data-testid={`ipt-doc-analyse-${doc.document_id}`}
      className="inline-flex items-center gap-1 text-[9.5px] font-semibold hover:underline"
      style={{ color: T.primary }}>
      <Sparkles className="w-3 h-3" /> analyse
    </button>
  );
};

export const MatchSuggestions = ({ d, T, onReload, compact = false }) => {
  const intel = d.docIntel || {};
  const all = intel.suggestions || [];
  const [working, setWorking] = useState(null);
  const tone = CONF_TONE(T);

  const decide = async (s, accept) => {
    setWorking(s.suggestion_id);
    try {
      await api.post(
        `/ipt/invoices/${d.invoice.invoice_id}/match-suggestions/${s.suggestion_id}/decide`,
        { accept });
      toast.success(accept
        ? 'Accepted — the measured quantity is filled and the decision is audited'
        : 'Rejected — recorded against the document');
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record that decision');
    } finally { setWorking(null); }
  };

  const body = all.length ? (
    <div className="space-y-2">
      {all.map((s) => {
        const decided = s.status !== 'PENDING';
        return (
          <div key={s.suggestion_id} data-testid={`ipt-suggestion-${s.suggestion_id}`}
            className="rounded-md px-3 py-2.5"
            style={{ background: T.surfaceAlt,
              border: `1px solid ${decided ? T.border : T.tint(T.primary, '44')}` }}>
            <div className="flex items-start gap-2 flex-wrap">
              <span className="px-1.5 rounded text-[9px] font-bold uppercase"
                style={{ background: T.tint(T.primary, '16'), color: T.primary }}>
                {(s.documentKind || '').replace(/_/g, ' ')}
              </span>
              <span className="text-[11px] font-semibold" style={{ color: T.ink }}>
                Line {s.line_no ?? '—'} · {s.lineDescription || s.readDescription}
              </span>
              <span className="px-1.5 rounded text-[9px] font-bold uppercase"
                style={{ background: T.tint(tone[s.confidence] || T.muted, '1F'),
                  color: tone[s.confidence] || T.muted }}>
                {s.confidence} confidence
              </span>
              {decided && (
                <span className="px-1.5 rounded text-[9px] font-bold uppercase"
                  data-testid={`ipt-suggestion-status-${s.suggestion_id}`}
                  style={{ background: T.tint(s.status === 'ACCEPTED' ? T.green : T.muted, '1F'),
                    color: s.status === 'ACCEPTED' ? T.green : T.muted }}>
                  {s.status}
                </span>
              )}
              {!decided && s.appliable && (
                <span className="ml-auto flex items-center gap-1.5">
                  <Btn T={T} variant="ok" testId={`ipt-suggestion-accept-${s.suggestion_id}`}
                    disabled={working === s.suggestion_id}
                    onClick={() => decide(s, true)}>
                    <Check className="w-3 h-3" /> accept
                  </Btn>
                  <Btn T={T} variant="ghost" testId={`ipt-suggestion-reject-${s.suggestion_id}`}
                    disabled={working === s.suggestion_id}
                    onClick={() => decide(s, false)}>
                    <X className="w-3 h-3" /> reject
                  </Btn>
                </span>
              )}
            </div>
            <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10.5px]">
              <Cell T={T} label="read off the document"
                value={`${num(s.suggestedQuantity, 3)} ${s.uom || ''}`} strong />
              <Cell T={T} label="vendor claimed"
                value={s.invoiceQuantity === null ? '—' : num(s.invoiceQuantity, 3)} />
              <Cell T={T} label="variance"
                value={s.quantityVariancePct === null ? '—'
                  : `${num(s.quantityVariance, 3)} (${num(s.quantityVariancePct, 2)}%)`}
                tone={Math.abs(s.quantityVariancePct || 0) > 2 ? T.red : T.body} />
              <Cell T={T} label="fills" value={(s.targetField || '—').replace(/_/g, ' ')} />
            </div>
            {s.evidence && (
              <p className="mt-1.5 text-[10px] flex items-start gap-1.5"
                data-testid={`ipt-suggestion-evidence-${s.suggestion_id}`}
                style={{ color: T.muted }}>
                <Quote className="w-3 h-3 mt-px shrink-0" />
                <span className="italic">{s.evidence}</span>
              </p>
            )}
            <p className="mt-1 text-[9.5px]" style={{ color: T.muted }}>
              {s.filename} · matched by {s.matchedBy || 'nothing — unmatched'}
              {s.appliable ? '' : ` · ${s.notAppliableReason}`}
            </p>
          </div>
        );
      })}
    </div>
  ) : (
    <Empty T={T} testId="ipt-suggestions-empty">
      No attachment has been read yet. Open the Documents tab and press
      {' '}<b>analyse</b> on a measurement sheet, GRN or service entry sheet — Cognara
      transcribes it and proposes the measured quantity for each line.
    </Empty>
  );

  if (compact) return body;
  return (
    <Panel T={T} testId="ipt-match-suggestions"
      title="Cognara — read from the attachments"
      subtitle={intel.rule}
      right={(
        <span className="flex items-center gap-2 text-[10px]" style={{ color: T.muted }}>
          <FileSearch className="w-3.5 h-3.5" />
          {intel.pending || 0} pending · {intel.accepted || 0} accepted ·
          {' '}{intel.rejected || 0} rejected
        </span>
      )}>
      {body}
    </Panel>
  );
};

const Cell = ({ label, value, tone, strong, T }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.06em]" style={{ color: T.muted }}>{label}</div>
    <div className="tabular-nums" style={{ color: tone || (strong ? T.ink : T.body),
      fontWeight: strong ? 600 : 400 }}>{value}</div>
  </div>
);
